import { ChangeJobFairPubsub } from 'app/app-events';
import { useAppContext } from 'app/context/app-context';
import { useAuth } from 'app/context/auth-context';
import { lastIDToken } from 'app/helpers/MiscUtils';
import { RxSubscription } from 'app/RxSubscriptions';
import { ActiveBoothViewCounterState } from 'app/services/BoothService';
import { VJFPCache } from 'app/services/CacheUtils';
// import { ActiveInterviewSessionState, InterviewSessionService } from 'app/services/InterviewSessionService';
import { timeService } from 'app/services/TimeService';
import { JobFair, JobFairStatus, Roles } from 'app/types';
import { config } from 'config';
import { useCallback, useEffect, useState } from 'react';
import { parse } from 'zipson';

type BackendSubscriptionCommand = {
  command: 'subscribe' | 'unsubscribe';
  subscriptionName: string;
  props: any;
};

const trace = (msg: unknown) => {
  if (localStorage.getItem('debug_trace') === 'true') {
    console.log(JSON.stringify(msg));
  }
};

const sendBackendSubscriptionCommand = (
  ws: WebSocket,
  subscriptionObj: BackendSubscriptionCommand,
  userId: number | string,
  jobFairId: string,
  organizationId: string
) => {
  if (ws.readyState !== WebSocket.OPEN) return;

  ws.send(JSON.stringify({
    action: subscriptionObj.command,
    subscriptionName: subscriptionObj.subscriptionName,
    props: subscriptionObj.props,
    userId,
    jobFairId,
    organizationId
  }));
};

function useWebSocket (): boolean | null {
  const [isSocketConnected, setIsSocketConnected] = useState<boolean | null>(null);
  const { user } = useAuth();
  const { jobFair } = useAppContext();

  // Wrap handleWebSocketOpen inside useCallback
  const handleWebSocketOpen = useCallback(async (ws: WebSocket) => {
    setIsSocketConnected(true);

    if (!user?.id || !jobFair?.id || !user?.organizationId) {
      console.log('user.id is null, so reloading the page');
      window.location.reload();
      return;
    }

    ws.send(`{"action": "setUserId", "userId": "${user.id}", "jobFairId": "${jobFair.id}" , "organizationId": "${user.organizationId}"}`);

    if (jobFair?.status === JobFairStatus.Started || jobFair?.status === JobFairStatus.ClosingSession) {
      let boothId: string | null = null;
      if (user?.roles?.includes(Roles.Recruiter)) {
        boothId = window.sessionStorage.getItem('recruiterBoothId');
      }

      const matrix = JSON.stringify({
        action: 'loginCount',
        userId: user?.id.toString(),
        jobFairId: jobFair.id,
        boothId,
        role: user?.roles?.toString()
      });
      ws.send(matrix);
    }

    RxSubscription.getActiveBackendSubscriptions().forEach(subscriptionObj => {
      sendBackendSubscriptionCommand(ws, subscriptionObj, user.id, jobFair.id, user.organizationId);
    });

    if (ws.readyState === 1 && user.roles) {
      if (user.roles.includes(Roles.Applicant)) { ws.send(`{"action": "ping", "userId": "${user.id}", "jobFairId": "${jobFair.id}", "organizationId": "${user.organizationId}", "role": "Applicant"}`); } else if (user.roles.includes(Roles.Recruiter)) { ws.send(`{"action": "ping", "userId": "${user.id}", "jobFairId": "${jobFair.id}", "organizationId": "${user.organizationId}", "role": "Recruiter", "boothId": "${window.sessionStorage.getItem('recruiterBoothId') || 0}" }`); }
    }
  }, [user?.id, user?.organizationId, user?.roles, jobFair?.id, jobFair?.status]);

  const handleWebSocketMessage = useCallback(async (event: MessageEvent) => {
    const data = event.data;
    let resp: ({ action?: string; value: any; timeStamp: string; interviewInvitePending: boolean; boothViewNeedRefreshCounter: number, failedPosts: any }) | undefined;
    try {
      resp = JSON.parse(data);
    } catch (err) {
      console.error(err);
    }
    if (!resp) return;

    const timeStamp = resp.timeStamp;
    if (resp.action?.startsWith('on')) {
      trace({ component: 'useWebsocket', event: 'ws_message_action', action: resp.action, valuePresenceStatus: resp.value?.presenceStatus, valueId: resp.value?.id });
      RxSubscription.notify(resp.action, resp.value);
    }

    if (resp.failedPosts) {
      trace({ component: 'useWebsocket', event: 'ws_message_failedPosts', failedPostsCount: resp.failedPosts?.length });
      const failedPosts = Array.isArray(resp.failedPosts) ? resp.failedPosts : [resp.failedPosts];
      for (const failedPost of failedPosts) {
        try {
          const value = typeof failedPost === 'string' ? parse(failedPost) : failedPost;
          if (value?.subscriptionName) {
            trace({ component: 'useWebsocket', event: 'ws_message_retry', subscriptionName: value?.subscriptionName, valueId: value?.id, presenceStatus: value?.presenceStatus });
            RxSubscription.notify(value.subscriptionName, value);
          }
        } catch (err) {
          console.error('Failed to parse websocket failed post', err);
        }
      }
    }

    if (resp.boothViewNeedRefreshCounter) {
      console.log('boothViewNeedRefreshCounter = ' + resp.boothViewNeedRefreshCounter);
      const currentCounter = ActiveBoothViewCounterState.getCurrentBoothViewCounter();
      if (resp.boothViewNeedRefreshCounter !== currentCounter) {
        ActiveBoothViewCounterState.setBoothViewCounter(Number(resp.boothViewNeedRefreshCounter));
      }
    }

    // if (resp.interviewInvitePending && jobFair?.id && user?.id && user.roles?.includes(Roles.Applicant)) {
    //   const interviewSessionService = new InterviewSessionService();
    //   if (!ActiveInterviewSessionState.getCurrentActiveInterviewSession()) {
    //     interviewSessionService.getActiveInterviewByJobseeker(jobFair.id, user.id).then(interviewSessions => {
    //       if (interviewSessions?.length && interviewSessions[0]) {
    //         ActiveInterviewSessionState.setInterviewSession(interviewSessions[0]);
    //         ChangeInterviewSessionPubsub.onAmplifyData({
    //           mode: 'create',
    //           value: interviewSessions[0]
    //         });
    //       }
    //     });
    //   }
    // }

    // Update server time for synchronization
    if (timeStamp) {
      // Server sends Unix timestamp in seconds (as string)
      const serverTimestamp = parseFloat(timeStamp);
      if (!isNaN(serverTimestamp)) {
        timeService.updateServerTime(serverTimestamp);
      }
    }

    if (timeStamp && jobFair) {
      ChangeJobFairPubsub.publish({
        mode: 'update',
        value: jobFair
      });
    };

    // Handle Booth List version update scenario

    if (jobFair) { _hanldeBoothListVersion(jobFair, resp); }
  }, [jobFair]);

  useEffect(() => {
    if (!user || !jobFair?.id) return;

    const ws = new WebSocket(config.socketUrl);

    const backendSubscription = RxSubscription.getBackendSubscriptionObservable().subscribe(subscriptionObj => {
      if (!subscriptionObj || !user?.id || !jobFair?.id || !user?.organizationId) return;
      sendBackendSubscriptionCommand(ws, subscriptionObj, user.id, jobFair.id, user.organizationId);
    });

    ws.onopen = () => handleWebSocketOpen(ws); // Pass the WebSocket instance

    ws.onmessage = handleWebSocketMessage;

    ws.onclose = () => {
      setIsSocketConnected(false);
    };

    ws.onerror = (err) => {
      setIsSocketConnected(false);
      console.error('Socket encountered error: ', JSON.stringify(err), 'Closing socket');
      ws.close();
      window.location.reload();
    };

    const sendDisconnectBeacon = () => {
      if (lastIDToken && user?.organizationId && user?.id && jobFair?.id) {
        const url = `${config.vjfpNGRestAPI}/vjfpUsers/${user.organizationId}/${user.id}`;
        const body = JSON.stringify({ id: user.id, presenceStatus: 'DISCONNECTED', lastPresenceJobFairId: jobFair.id });
        trace({ component: 'useWebsocket', event: 'sendDisconnectBeacon', url, body });
        fetch(url, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json', Authorization: lastIDToken },
          body,
          keepalive: true
        }).then(() => {
          trace({ component: 'useWebsocket', event: 'sendDisconnectBeacon_success' });
        }).catch((err) => {
          trace({ component: 'useWebsocket', event: 'sendDisconnectBeacon_failed', error: err?.message });
        });
      } else {
        trace({ component: 'useWebsocket', event: 'sendDisconnectBeacon_skipped', hasToken: !!lastIDToken, hasOrgId: !!user?.organizationId, hasUserId: !!user?.id, hasJobFairId: !!jobFair?.id });
      }
    };

    // Handle cleanup before page unload
    window.onbeforeunload = () => {
      if (ws) {
        ws.onclose = null; // Disable onclose handler
        if (ws.readyState <= 1) {
          ws.close();
        }
      }
      sendDisconnectBeacon();
    };

    const intervalId = setInterval(() => {
      if (ws && ws.readyState === 1 && user.roles) {
        if (user.roles.includes(Roles.Applicant)) { ws.send(`{"action": "ping", "userId": "${user.id}", "jobFairId": "${jobFair.id}", "organizationId": "${user.organizationId}", "role": "Applicant"}`); } else if (user.roles.includes(Roles.Recruiter)) { ws.send(`{"action": "ping", "userId": "${user.id}", "jobFairId": "${jobFair.id}", "organizationId": "${user.organizationId}", "role": "Recruiter", "boothId": "${window.sessionStorage.getItem('recruiterBoothId') || 0}"}`); }
      }
    }
    , 10000); // 10 seconds in milliseconds

    return () => {
      clearInterval(intervalId);
      backendSubscription.unsubscribe();
      ws.close();
      window.onbeforeunload = null;
    };
  }, [handleWebSocketMessage, handleWebSocketOpen, user, jobFair?.id]);

  return isSocketConnected;
}

export default useWebSocket;

function _hanldeBoothListVersion (jobFair: JobFair, resp: any) {
  const localBoothListVersionKey = `localBoothListVersion_${jobFair.id}`;
  const localBoothListVersionValue = window.sessionStorage.getItem(localBoothListVersionKey);
  if (localBoothListVersionValue && Number(localBoothListVersionValue) !== Number(resp.boothListVersion)) {
    // Clear Cache if local booth version do not match with updated one
    VJFPCache.removeItem(`BOOTHS_${jobFair.id}`);
    // update local booth version with the updated one
    window.sessionStorage.setItem(localBoothListVersionKey, resp.boothListVersion);
  } else if (localBoothListVersionValue == null) {
    // Set the local booth version with Booth List version if localBoothVersion is not set
    window.sessionStorage.setItem(localBoothListVersionKey, resp.boothListVersion);
  }
}
